import { isArray } from '@rehaje/shared'

import { UnknownSchema } from '../../constants'
import {
  SchemaMaximumError,
  SchemaMinimumError,
  SchemaTransformError
} from '../../errors'
import { normalizeNumberString } from '../../lambdas/normalizeNumberString'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type { RuntimeSchemaSpecific, SchemaNumberSpecific } from '../../types'

export class SchemaNumberDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaNumberSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default
    }
    return value
  }

  override getIn(schema: SchemaNumberSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaNumberSpecific
  ): SchemaNumberSpecific {
    return { ...schema, type: 'Schema.Number', value: target }
  }

  override inferOnlySchema(schema: SchemaNumberSpecific) {
    return schema
  }

  override inferTypeScript(schema: SchemaNumberSpecific): string {
    const value = schema.value
    if (value !== undefined && this.test(value, schema))
      return JSON.stringify(value)
    return 'number'
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaNumberSpecific,
    path: PropertyKey[] = []
  ) {
    return this.transformToNumber(target, schema, path)
  }

  transformToNumber(
    value: any,
    schema: SchemaNumberSpecific,
    path: PropertyKey[] = []
  ) {
    if (value === null && schema.nullable) return value
    const result = this.transformToNumberNullable(value, schema, path)
    if (typeof result === 'number' || (result === null && schema.nullable))
      return result
    return schema.fallback === undefined ? 0 : schema.fallback
  }

  transformToNumberNullable(
    value: any,
    schema: SchemaNumberSpecific,
    path: PropertyKey[] = []
  ): null | number {
    if (Number.isNaN(value)) {
      return 0
    }
    if (isArray(value) && value.length === 1) {
      return this.transformToNumberNullable(value[0], schema, path)
    }
    if (typeof value === 'string') {
      value = normalizeNumberString(value)
    }
    const number = Number(value)
    if (isNaN(number)) {
      if (value !== schema.fallback)
        this.raise(new SchemaTransformError(schema.name))
      return schema.fallback ?? null
    }
    return number
  }

  override validate(target: any, schema: any): void
  override validate(
    target: any,
    schema: RuntimeSchemaSpecific<SchemaNumberSpecific>
  ): void {
    if (this.validateMetadata(target, schema, 'number')) return
    if (schema.minimum && target < schema.minimum) {
      this.raise(new SchemaMinimumError(schema.name, schema.minimum))
    }
    if (schema.maximum && target > schema.maximum) {
      this.raise(new SchemaMaximumError(schema.name, schema.maximum))
    }
  }
}
